import React, { useState, useEffect, useMemo } from 'react';
import { X, QrCode, Copy, CheckCircle2, Loader2, MessageSquare, Send, Check, GraduationCap, Target, ThumbsUp, AlertTriangle } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { api } from '../lib/api';
import { PROGRESSION } from '../data/progressionAutoEcole';
import { MOTO_PROGRESSION } from '../data/progressionMoto';
import { getPublicOrigin } from '../lib/publicUrl';

const buildFlat = (prog) => {
  const out = [];
  (prog || []).forEach((phase) => {
    (phase.steps || []).forEach((s) => out.push({ id: s.id, label: s.label, phase: phase.phase }));
  });
  return out;
};
const UNIVERS_DATA = { voiture: buildFlat(PROGRESSION), moto: buildFlat(MOTO_PROGRESSION) };

const EVAL_META = {
  vu:       { label: 'Observé', short: '👁', chip: 'bg-sky-500/15 border-sky-500/50 text-sky-200' },
  compris:  { label: 'Compris', short: '💡', chip: 'bg-violet-500/15 border-violet-500/50 text-violet-200' },
  acquis:   { label: 'Acquis',  short: '✅', chip: 'bg-emerald-500/15 border-emerald-500/50 text-emerald-200' },
  a_revoir: { label: 'À revoir', short: '🔄', chip: 'bg-amber-500/15 border-amber-500/50 text-amber-200' },
};
const EVAL_ORDER = ['vu', 'compris', 'acquis', 'a_revoir'];

const PLUS_TAGS = ['Bonne observation', 'Bonne maîtrise du véhicule', 'Allure adaptée', 'Bons contrôles rétros', 'Trajectoires précises', 'Bonne anticipation', 'Attitude positive', 'Net progrès'];
const MOINS_TAGS = ['Vitesse à adapter', 'Contrôles rétros à renforcer', 'Angles morts oubliés', 'Anticipation à travailler', 'Position sur la chaussée', 'Regard à porter plus loin', 'Manque de confiance', 'Précipitation'];

function TagPicker({ tags, selected, onToggle, free, onFree, color, testPrefix }) {
  return (
    <div>
      <div className="flex flex-wrap gap-1.5">
        {tags.map((t) => {
          const active = selected.includes(t);
          return (
            <button key={t} type="button" onClick={() => onToggle(t)} data-testid={`${testPrefix}-tag-${tags.indexOf(t)}`}
              className={`px-2.5 py-1.5 rounded-full border text-[11.5px] font-semibold transition-colors ${active ? color : 'bg-white/[0.03] border-white/10 text-white/55 hover:border-white/25'}`}>
              {t}
            </button>
          );
        })}
      </div>
      <input value={free} onChange={(e) => onFree(e.target.value)} maxLength={120} data-testid={`${testPrefix}-free`}
        placeholder="Autre… (texte libre)"
        className="mt-2 w-full h-9 rounded-lg bg-white/[0.03] border border-white/10 focus:border-white/30 outline-none px-3 text-white text-[12.5px]" />
    </div>
  );
}

export default function FinDeCoursModal({ open, onClose, initialStudentName = '', initialUnivers = 'voiture' }) {
  const [studentName, setStudentName] = useState('');
  const [univers, setUnivers] = useState('voiture');
  const [evals, setEvals] = useState({});           // { stepId: 'vu'|'compris'|'acquis'|'a_revoir' }
  const [plusTags, setPlusTags] = useState([]);
  const [moinsTags, setMoinsTags] = useState([]);
  const [plusFree, setPlusFree] = useState('');
  const [moinsFree, setMoinsFree] = useState('');
  const [nextStepId, setNextStepId] = useState('');
  const [nextTouched, setNextTouched] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [err, setErr] = useState('');
  const [copiedShort, setCopiedShort] = useState(false);
  const [copiedFull, setCopiedFull] = useState(false);

  useEffect(() => { if (open) { setStudentName(initialStudentName || ''); setUnivers(initialUnivers || 'voiture'); } }, [open, initialStudentName, initialUnivers]);

  const ALL_STEPS = UNIVERS_DATA[univers] || UNIVERS_DATA.voiture;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const PHASES = useMemo(() => Array.from(new Set(ALL_STEPS.map((s) => s.phase))), [univers]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const STEP_INDEX = useMemo(() => Object.fromEntries(ALL_STEPS.map((s, i) => [s.id, i])), [univers]);

  const switchUnivers = (u) => {
    if (u === univers) return;
    setUnivers(u); setEvals({}); setNextTouched(false); setNextStepId('');
  };

  const setEval = (id, val) => setEvals((prev) => {
    const n = { ...prev };
    if (n[id] === val) delete n[id]; else n[id] = val;
    return n;
  });

  // Proposition auto du prochain cours : priorité aux « à revoir », sinon l'étape suivante du plan
  const autoNext = useMemo(() => {
    const ids = Object.keys(evals);
    if (ids.length === 0) return null;
    const revoir = ids.filter((id) => evals[id] === 'a_revoir').sort((a, b) => STEP_INDEX[a] - STEP_INDEX[b]);
    if (revoir.length > 0) return ALL_STEPS[STEP_INDEX[revoir[0]]];
    const lastIdx = Math.max(...ids.map((id) => STEP_INDEX[id] ?? -1));
    return ALL_STEPS[lastIdx + 1] || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evals, univers]);

  useEffect(() => { if (!nextTouched) setNextStepId(autoNext?.id || ''); }, [autoNext, nextTouched]);

  const nextStep = ALL_STEPS.find((s) => s.id === nextStepId) || null;
  const selectedCount = Object.keys(evals).length;
  const firstName = (studentName || '').trim().split(/\s+/)[0] || '';
  const allPlus = [...plusTags, ...(plusFree.trim() ? [plusFree.trim()] : [])];
  const allMoins = [...moinsTags, ...(moinsFree.trim() ? [moinsFree.trim()] : [])];

  const submit = async () => {
    setErr('');
    if (selectedCount === 0) { setErr('Évalue au moins une compétence travaillée.'); return; }
    setCreating(true);
    try {
      const res = await api.flashSeanceCreate({
        student_name: studentName.trim() || null,
        steps: Object.entries(evals).map(([id, status]) => ({ id, status })),
        notes: '',
        duration_days: 30,
        kind: 'bilan',
        plus: allPlus,
        moins: allMoins,
        next_step: nextStep ? { id: nextStep.id, label: nextStep.label } : null,
      });
      setCreated(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Erreur lors de la création.');
    } finally { setCreating(false); }
  };

  const publicUrl = created ? `${getPublicOrigin()}/s/${created.token}` : '';
  const dateFr = new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

  const groupIds = (st) => Object.keys(evals).filter((id) => evals[id] === st).sort((a, b) => STEP_INDEX[a] - STEP_INDEX[b]);

  // Version COURTE (limite de mots — collable dans l'app Codes Rousseau)
  const shortText = useMemo(() => {
    const parts = [`${firstName || 'Élève'} · ${dateFr}`];
    EVAL_ORDER.forEach((st) => {
      const ids = groupIds(st);
      if (ids.length) parts.push(`${EVAL_META[st].short} ${ids.join(' ')}`);
    });
    if (allPlus.length) parts.push(`+ ${allPlus.slice(0, 2).join(', ')}`);
    if (allMoins.length) parts.push(`− ${allMoins.slice(0, 2).join(', ')}`);
    if (nextStep) parts.push(`▶ Prochain: ${nextStep.id} ${nextStep.label}`);
    return parts.join('\n');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evals, allPlus, allMoins, nextStep, firstName, univers]);

  // Version COMPLÈTE personnalisée (WhatsApp / SMS)
  const fullText = useMemo(() => {
    const lines = [`Bonjour ${firstName || ''} 👋`.trim() + ' !', '', `Voici le bilan de ta leçon du ${dateFr} :`];
    EVAL_ORDER.forEach((st) => {
      const ids = groupIds(st);
      if (ids.length) {
        lines.push('');
        lines.push(`${EVAL_META[st].short} ${EVAL_META[st].label} :`);
        ids.forEach((id) => lines.push(`• ${id} · ${ALL_STEPS[STEP_INDEX[id]]?.label || id}`));
      }
    });
    if (allPlus.length) { lines.push(''); lines.push(`👍 Les + : ${allPlus.join(', ')}`); }
    if (allMoins.length) { lines.push(`⚠️ À travailler : ${allMoins.join(', ')}`); }
    if (nextStep) { lines.push(''); lines.push(`🎯 Prochain cours : ${nextStep.id} · ${nextStep.label}`); }
    if (publicUrl) { lines.push(''); lines.push(`📲 Ton récap interactif + ton ressenti : ${publicUrl}`); }
    return lines.join('\n');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evals, allPlus, allMoins, nextStep, firstName, publicUrl, univers]);

  const copyTxt = async (txt, setFlag) => {
    try { await navigator.clipboard.writeText(txt); setFlag(true); setTimeout(() => setFlag(false), 2000); } catch (_) { /* silent */ }
  };

  const reset = () => { setCreated(null); setEvals({}); setPlusTags([]); setMoinsTags([]); setPlusFree(''); setMoinsFree(''); setNextTouched(false); setErr(''); };

  if (!open) return null;
  const now = new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center bg-black/70 backdrop-blur-sm overflow-y-auto p-3 sm:p-6" onClick={onClose} data-testid="fin-cours-modal">
      <div className="relative w-full max-w-2xl bg-[#0f0f10] border border-white/10 rounded-2xl shadow-2xl my-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="fin-cours-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 text-white/70 hover:text-white flex items-center justify-center z-10"><X size={18} /></button>

        <div className="p-5 sm:p-7">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-400 flex items-center justify-center"><GraduationCap size={18} /></div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-amber-400">Fin de cours · bilan guidé</div>
              <h3 className="font-serif-editorial text-white text-[22px] sm:text-[26px] leading-tight">{firstName ? `Bilan de ${firstName}` : 'Bilan de la leçon'}</h3>
            </div>
          </div>

          {!created ? (
            <div className="mt-5 space-y-6">
              {/* Univers */}
              <div className="flex gap-2" data-testid="fin-univers-toggle">
                {[{ k: 'voiture', label: '🚗 Voiture · 23 étapes' }, { k: 'moto', label: '🏍 Moto · 38 compétences REMC' }].map((u) => (
                  <button key={u.k} type="button" onClick={() => switchUnivers(u.k)} data-testid={`fin-univers-${u.k}`}
                    className={`flex-1 h-10 rounded-full border text-[12.5px] font-bold transition-colors ${univers === u.k
                      ? (u.k === 'moto' ? 'bg-sky-500/20 border-sky-400/60 text-sky-200' : 'bg-red-500/15 border-red-400/60 text-red-200')
                      : 'bg-white/[0.03] border-white/10 text-white/50 hover:border-white/25'}`}>
                    {u.label}
                  </button>
                ))}
              </div>
              {/* Élève */}
              <div>
                <label className="text-white/60 text-[11px] font-mono-spec uppercase tracking-wider font-bold">Prénom de l&apos;élève</label>
                <input value={studentName} onChange={(e) => setStudentName(e.target.value)} maxLength={60} data-testid="fin-cours-student"
                  placeholder="Ex. : Léa" className="mt-1.5 w-full h-11 rounded-lg bg-white/[0.03] border border-white/10 focus:border-amber-500/60 outline-none px-3 text-white text-[14px]" />
              </div>

              {/* 1 — Compétences + échelle Observé/Compris/Acquis/À revoir */}
              <div>
                <div className="text-white/60 text-[11px] font-mono-spec uppercase tracking-wider font-bold">
                  1 · Compétences travaillées · <span className="text-amber-400">{selectedCount}</span> évaluée{selectedCount > 1 ? 's' : ''}
                </div>
                <div className="mt-1 flex flex-wrap gap-2 text-[10.5px] font-mono-spec text-white/50">
                  {EVAL_ORDER.map((st) => <span key={st}>{EVAL_META[st].short} {EVAL_META[st].label}</span>)}
                </div>
                <div className="mt-2 max-h-60 overflow-y-auto rounded-lg border border-white/10 bg-white/[0.02] p-2 space-y-3" data-testid="fin-cours-steps">
                  {PHASES.map((phase) => (
                    <div key={phase}>
                      <div className="text-[10px] font-mono-spec uppercase tracking-widest text-white/40 px-1 mb-1.5">{phase}</div>
                      <div className="space-y-1">
                        {ALL_STEPS.filter((s) => s.phase === phase).map((s) => {
                          const cur = evals[s.id];
                          const meta = cur ? EVAL_META[cur] : null;
                          return (
                            <div key={s.id} data-testid={`fin-step-${s.id}`} className={`flex items-center gap-2 rounded-md border transition-colors ${meta ? meta.chip.replace('text-', 'x-') && `${meta.chip.split(' ')[0]} ${meta.chip.split(' ')[1]}` : 'bg-white/[0.03] border-white/10'}`}>
                              <div className="flex-1 px-2.5 py-1.5 text-[11.5px] leading-tight">
                                <span className="font-mono-spec font-bold text-white">{s.id}</span>{' '}
                                <span className={meta ? 'text-white' : 'text-white/60'}>{s.label.length > 34 ? s.label.slice(0, 32) + '…' : s.label}</span>
                              </div>
                              <div className="flex items-center pr-1 gap-0.5 shrink-0">
                                {EVAL_ORDER.map((st) => (
                                  <button key={st} type="button" onClick={() => setEval(s.id, st)} data-testid={`fin-step-${s.id}-${st}`} title={EVAL_META[st].label}
                                    className={`h-6 w-6 rounded-full text-[11px] leading-none flex items-center justify-center border ${cur === st ? `${EVAL_META[st].chip} ring-1 ring-white/30` : 'bg-transparent border-white/10 hover:border-white/25'}`}>
                                    {EVAL_META[st].short}
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 2 — Les + / Les − */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-emerald-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5 mb-2"><ThumbsUp size={12} /> 2 · Les points forts</div>
                  <TagPicker tags={PLUS_TAGS} selected={plusTags} onToggle={(t) => setPlusTags((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t])}
                    free={plusFree} onFree={setPlusFree} color="bg-emerald-500/15 border-emerald-500/50 text-emerald-200" testPrefix="fin-plus" />
                </div>
                <div>
                  <div className="text-amber-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5 mb-2"><AlertTriangle size={12} /> Les points à travailler</div>
                  <TagPicker tags={MOINS_TAGS} selected={moinsTags} onToggle={(t) => setMoinsTags((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t])}
                    free={moinsFree} onFree={setMoinsFree} color="bg-amber-500/15 border-amber-500/50 text-amber-200" testPrefix="fin-moins" />
                </div>
              </div>

              {/* 3 — Prochaine leçon */}
              <div>
                <div className="text-sky-300 text-[11px] font-mono-spec uppercase tracking-wider font-bold inline-flex items-center gap-1.5"><Target size={12} /> 3 · Proposition du prochain cours</div>
                {autoNext && !nextTouched && nextStepId === autoNext.id && (
                  <div className="mt-1 text-white/50 text-[11.5px]">Proposée automatiquement selon le plan de progression{evals[autoNext.id] === 'a_revoir' ? ' (étape à revoir en priorité)' : ''}.</div>
                )}
                <select value={nextStepId} onChange={(e) => { setNextStepId(e.target.value); setNextTouched(true); }} data-testid="fin-next-select"
                  className="mt-2 w-full h-11 rounded-lg bg-[#141416] border border-white/10 focus:border-sky-500/60 outline-none px-3 text-white text-[13px]">
                  <option value="">— Aucune proposition —</option>
                  {ALL_STEPS.map((s) => <option key={s.id} value={s.id}>{s.id} · {s.label}</option>)}
                </select>
              </div>

              {err && <div className="text-red-400 text-[13px] bg-red-500/10 border border-red-500/25 rounded-md p-2.5" data-testid="fin-cours-error">{err}</div>}

              <button type="button" onClick={submit} disabled={creating} data-testid="fin-cours-submit"
                className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black font-bold text-[14px] inline-flex items-center justify-center gap-2 shadow-lg shadow-amber-500/25">
                {creating ? <><Loader2 size={16} className="animate-spin" /> Génération…</> : <><QrCode size={16} /> Générer le bilan (QR + texte)</>}
              </button>
            </div>
          ) : (
            <div className="mt-5" data-testid="fin-cours-created">
              <div className="text-center">
                <div className="inline-flex items-center gap-2 text-emerald-400 text-[12px] font-mono-spec font-bold bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1">
                  <CheckCircle2 size={14} /> Bilan créé{created.auto_claimed ? ` · intégré au parcours de ${created.student_account_name}` : ''}
                </div>
              </div>

              {/* QR */}
              <div className="mt-4 flex flex-col sm:flex-row gap-4 items-center">
                <div className="w-[168px] p-3 bg-white rounded-2xl shadow-xl shrink-0" data-testid="fin-cours-qr">
                  <QRCodeSVG value={publicUrl} size={144} level="H" includeMargin={false} />
                </div>
                <div className="flex-1 text-white/65 text-[12.5px] leading-relaxed">
                  <b className="text-white">L&apos;élève scanne ce QR</b> : il revoit ses compétences (observé · compris · acquis · à revoir), tes + et −, le prochain cours proposé — et peut <b className="text-white">donner son propre ressenti</b> que tu verras en rouvrant la fiche.
                  <div className="mt-2 flex gap-1.5">
                    <input readOnly value={publicUrl} onClick={(e) => e.target.select()} data-testid="fin-cours-url"
                      className="flex-1 h-9 rounded-lg bg-white/[0.04] border border-white/10 outline-none px-2.5 text-white text-[11.5px] font-mono-spec" />
                    <button onClick={() => copyTxt(publicUrl, setCopiedShort)} className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/15 text-white text-[11.5px] font-semibold">
                      {copiedShort ? '✓' : <Copy size={13} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Version courte — Codes Rousseau */}
              <div className="mt-5 rounded-xl border border-white/10 overflow-hidden">
                <div className="flex items-center justify-between px-3.5 py-2 bg-white/[0.04]">
                  <div className="text-white/70 text-[11px] font-mono-spec uppercase tracking-wider font-bold">Version courte · app Codes Rousseau</div>
                  <button onClick={() => copyTxt(shortText, setCopiedShort)} data-testid="fin-copy-short"
                    className={`h-8 px-3 rounded-full text-[11.5px] font-bold inline-flex items-center gap-1.5 transition ${copiedShort ? 'bg-emerald-500 text-emerald-950' : 'bg-white/10 hover:bg-white/15 text-white'}`}>
                    {copiedShort ? <><Check size={12} /> Copié !</> : <><Copy size={12} /> Copier</>}
                  </button>
                </div>
                <pre className="whitespace-pre-wrap font-sans text-[12px] leading-relaxed text-white/85 m-0 px-3.5 py-3 bg-black/30" data-testid="fin-short-text">{shortText}</pre>
              </div>

              {/* Version complète — aperçu WhatsApp */}
              <div className="mt-4 rounded-xl overflow-hidden border border-white/10">
                <div className="flex items-center gap-2.5 px-3.5 py-2 bg-[#1f2c34]">
                  <div className="w-7 h-7 rounded-full bg-emerald-600 flex items-center justify-center text-white shrink-0"><GraduationCap size={13} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[12.5px] font-semibold leading-tight">{firstName || 'Ton élève'}</div>
                    <div className="text-white/45 text-[10px]">version complète · aperçu avant envoi</div>
                  </div>
                </div>
                <div className="px-3 py-3 max-h-[32vh] overflow-y-auto" style={{ background: '#0b141a', backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)', backgroundSize: '18px 18px' }}>
                  <div className="flex justify-end">
                    <div className="relative max-w-[92%] rounded-xl rounded-tr-sm px-3 py-2 shadow-md" style={{ background: '#005c4b' }} data-testid="fin-full-bubble">
                      <pre className="whitespace-pre-wrap font-sans text-[12px] leading-relaxed text-white/95 m-0">{fullText}</pre>
                      <div className="flex items-center justify-end gap-1 mt-1 text-[10px] text-white/55">{now}<Check size={12} className="text-sky-300 -mr-2" /><Check size={12} className="text-sky-300" /></div>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 px-3.5 py-2.5 bg-[#1f2c34]">
                  <button onClick={() => copyTxt(fullText, setCopiedFull)} data-testid="fin-copy-full"
                    className={`h-9 px-3.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 transition ${copiedFull ? 'bg-emerald-500 text-emerald-950' : 'bg-white/10 hover:bg-white/15 text-white border border-white/15'}`}>
                    {copiedFull ? <><Check size={12} /> Copié !</> : <><Copy size={12} /> Copier</>}
                  </button>
                  <a href={`sms:?&body=${encodeURIComponent(fullText)}`} data-testid="fin-sms" className="h-9 px-3.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 bg-sky-600 hover:bg-sky-500 text-white transition"><MessageSquare size={12} /> SMS</a>
                  <a href={`https://wa.me/?text=${encodeURIComponent(fullText)}`} target="_blank" rel="noopener noreferrer" data-testid="fin-whatsapp" className="h-9 px-3.5 rounded-full text-[12px] font-bold inline-flex items-center gap-1.5 text-white transition" style={{ background: '#25D366' }}><Send size={12} /> WhatsApp</a>
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-2">
                <button onClick={reset} data-testid="fin-cours-new" className="h-11 rounded-full bg-white/10 hover:bg-white/15 text-white text-[13px] font-semibold">Nouveau bilan</button>
                <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="fin-cours-preview" className="h-11 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-[13px] font-bold inline-flex items-center justify-center gap-1.5">Voir la fiche élève</a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
